import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Plus } from "lucide-react";

const TYPES = [
  {
    key: "semi-hardwood",
    number: "01",
    name: "Semi-Hardwood",
    image: "/generated/types/semi_hardwood.png",
    short: "Balanced strength, everyday workhorse",
    description:
      "A composite of hardwood and softwood layers, offering reliable strength at a competitive price. Popular for modular furniture, partitions and general carpentry.",
    points: ["Mixed core construction", "Furniture · partitions", "Cost-efficient"],
    accent: "#C19372",
  },
  {
    key: "gurjan-face",
    number: "02",
    name: "Gurjan Face",
    image: "/generated/types/gurjan_face.png",
    short: "Signature reddish-brown grain",
    description:
      "Premium Gurjan veneer face over a tough hardwood core. Loved by designers for its rich grain pattern and excellent screw-hold.",
    points: ["Gurjan veneer face", "Designer-grade finish", "Strong screw retention"],
    accent: "#8A4B2D",
  },
  {
    key: "neem-ply",
    number: "03",
    name: "Neem Ply",
    image: "/generated/types/neem_ply.png",
    short: "Naturally insect-resistant",
    description:
      "Built around neem wood layers, naturally resistant to termites and borers. The smart choice for humid spaces and long-lasting interiors.",
    points: ["Termite & borer resistant", "Moisture tolerant", "Long service life"],
    accent: "#C9A560",
  },
  {
    key: "hardwood",
    number: "04",
    name: "Hardwood",
    image: "/generated/types/hardwood.png",
    short: "Maximum strength, full hardwood core",
    description:
      "100% hardwood core construction with no softwood fillers — the highest density and load-bearing capacity in our range. Built for heavy-duty work.",
    points: ["Full hardwood layers", "Heavy load applications", "Best screw-hold"],
    accent: "#5A3A22",
  },
  {
    key: "gurjan-ply",
    number: "05",
    name: "Gurjan Ply",
    image: "/generated/types/gurjan_ply.png",
    short: "The premium marine-grade benchmark",
    description:
      "Full Gurjan construction — face and core both — delivering exceptional moisture resistance and density. The standard for marine plywood and luxury interior work.",
    points: ["Full Gurjan layers", "Marine-grade BWP options", "Top-tier durability"],
    accent: "#6B2E1F",
  },
];

const PlywoodTypes = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      id="types"
      data-testid="types-section"
      className="py-24 sm:py-32 bg-white border-y border-[color:var(--nitc-border)]"
    >
      <div className="container-nitc">
        <Reveal className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-7">
            <p className="text-eyebrow">Types of plywood</p>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--nitc-ink)] leading-[1.05] tracking-tight">
              Five constructions.<br />
              One trusted dealer.
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:items-end">
            <p className="text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed">
              From everyday semi-hardwood to premium marine-grade Gurjan, our shelves are stocked with
              every grade you need — hand-picked from India's most trusted mills.
            </p>
          </div>
        </Reveal>

        {/* Desktop: expanding horizontal panels */}
        <div
          className="hidden md:flex gap-3 h-[520px] lg:h-[600px]"
          data-testid="types-panels"
          onMouseLeave={() => setActive(0)}
        >
          {TYPES.map((t, i) => {
            const isActive = i === active;
            return (
              <motion.div
                key={t.key}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                tabIndex={0}
                data-testid={`type-panel-${t.key}`}
                role="button"
                aria-pressed={isActive}
                animate={{ flexGrow: isActive ? 5 : 1 }}
                transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="relative flex-1 basis-0 min-w-0 overflow-hidden rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-[color:var(--nitc-accent)]"
                style={{ backgroundColor: t.accent }}
              >
                <motion.img
                  src={t.image}
                  alt={t.name}
                  initial={false}
                  animate={{ scale: isActive ? 1 : 1.15, opacity: isActive ? 1 : 0.55 }}
                  transition={{ duration: 1.1, ease: [0.21, 0.47, 0.32, 0.98] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {/* Gradient overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: isActive
                      ? "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.78) 100%)"
                      : "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.82) 100%)",
                    transition: "background 0.8s ease",
                  }}
                />

                {/* Number badge top-left */}
                <div className="absolute top-5 left-5 right-5 flex items-start justify-between text-white">
                  <span className="font-display text-sm font-medium opacity-70">{t.number}</span>
                  <motion.span
                    animate={{ rotate: isActive ? 45 : 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-7 w-7 rounded-full bg-white/15 backdrop-blur flex items-center justify-center"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </motion.span>
                </div>

                {/* Collapsed: rotated vertical name */}
                {!isActive && (
                  <motion.div
                    initial={false}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 flex items-end justify-center pb-8"
                  >
                    <p
                      className="font-display text-white text-base sm:text-lg font-medium tracking-wide whitespace-nowrap"
                      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                      {t.name}
                    </p>
                  </motion.div>
                )}

                {/* Expanded content */}
                <motion.div
                  initial={false}
                  animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
                  transition={{ duration: 0.55, delay: isActive ? 0.25 : 0 }}
                  className="absolute bottom-0 left-0 right-0 p-7 sm:p-9 text-white"
                >
                  <p
                    className="text-eyebrow"
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {t.short}
                  </p>
                  <h3 className="mt-3 font-display text-3xl sm:text-4xl font-semibold leading-tight">
                    {t.name}
                  </h3>
                  <p className="mt-4 max-w-md text-sm sm:text-base text-white/85 leading-relaxed">
                    {t.description}
                  </p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {t.points.map((p) => (
                      <li
                        key={p}
                        className="text-[11px] tracking-[0.12em] uppercase font-medium text-white/90 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/15"
                      >
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile: vertically stacked cards */}
        <div className="md:hidden flex flex-col gap-4">
          {TYPES.map((t) => (
            <Reveal key={t.key}>
              <div
                data-testid={`type-card-mobile-${t.key}`}
                className="relative h-72 overflow-hidden rounded-lg ring-1 ring-black/5"
                style={{ backgroundColor: t.accent }}
              >
                <img
                  src={t.image}
                  alt={t.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.82) 100%)",
                  }}
                />
                <div className="absolute top-4 left-4 text-white text-xs opacity-70">
                  {t.number}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <p
                    className="text-eyebrow"
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {t.short}
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-semibold">{t.name}</h3>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed">{t.description}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlywoodTypes;
